import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import DataTable from 'react-data-table-component';
import { createPortal } from 'react-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { useGetLoanBookRecords, useGetLoanBookSummary, recordWriteOff } from '../../../services/loanService';
import type { LoanBookRecord } from '../../../services/schemas/loanServiceSchemas';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import { getComments, addComment } from '../../../services/loanCommentService';
import { getLedgerAccountById } from '../../../services/loanService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import './stats-cards.css';
import { toLocalDateString } from '../../../utils/localDate';

interface LatestCommentMap {
  [recordId: string]: string;
}

interface ApplicationIdMap {
  [recordId: string]: string;
}

// Helper to determine if a loan book record is a charge entry (added after disbursement)
// Charge entry: loanAmount === 0 AND parentReferenceNo !== null AND parentReferenceNo !== refNo
// Loan entry: loanAmount > 0 OR parentReferenceNo === refNo OR parentReferenceNo === null
const isChargeEntry = (record: LoanBookRecord): boolean => {
  return (
    (record.loanAmount === 0 || record.loanAmount === undefined) &&
    record.parentReferenceNo !== null &&
    record.parentReferenceNo !== undefined &&
    record.parentReferenceNo !== record.refNo
  );
};

const LoanInvoices = () => {
  const [size] = useState(10);
  const ALL_PAGE_SIZE = 10000;
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [customerName, setCustomerName] = useState('');
  const [filterInvoicedOnSage, setFilterInvoicedOnSage] = useState<boolean | undefined>(undefined);
  const [filterType, setFilterType] = useState<'all' | 'loan' | 'charge'>('all');
  const [actionLoadingState, setActionLoadingState] = useState<Record<string, 'disbursed' | 'invoiced' | 'writeoff' | null>>({});
  const [statusOverrides, setStatusOverrides] = useState<Record<string, Pick<LoanBookRecord, 'disbursed' | 'invoicedOnSage' | 'status'>>>({});
  const [sageInvoiceModalOpen, setSageInvoiceModalOpen] = useState(false);
  const [sageInvoiceNumber, setSageInvoiceNumber] = useState('');
  const [sageInvoiceTarget, setSageInvoiceTarget] = useState<LoanBookRecord | null>(null);
  const [latestComments, setLatestComments] = useState<LatestCommentMap>({});
  const [applicationIdMap, setApplicationIdMap] = useState<ApplicationIdMap>({});
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentTarget, setCommentTarget] = useState<LoanBookRecord | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [viewCommentsModalOpen, setViewCommentsModalOpen] = useState(false);
  const [viewCommentsTarget, setViewCommentsTarget] = useState<LoanBookRecord | null>(null);
  const [allComments, setAllComments] = useState<Array<{ commentText: string; createdBy?: string; createdDate?: string }>>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [sageInfoModalOpen, setSageInfoModalOpen] = useState(false);
  const [sageInfoTarget, setSageInfoTarget] = useState<LoanBookRecord | null>(null);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const queryClient = useQueryClient();

  const targetMonth = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}-01`;

  const queryParams = {
    page: 0,
    size: ALL_PAGE_SIZE,
    targetMonth,
    ...(customerName.trim() && { customerName: customerName.trim() }),
    ...(filterInvoicedOnSage !== undefined && { invoicedOnSage: filterInvoicedOnSage }),
  };

  const { data: loanBookData, isLoading, error, refetch } = useGetLoanBookRecords(queryParams);
  const { data: loanBookSummary } = useGetLoanBookSummary(
    { targetMonth },
    { query: { enabled: !!targetMonth } }
  );

  useEffect(() => {
    if (error) {
      console.error('Error fetching loan invoices data:', error);
      toast.error('Failed to fetch loan invoices data');
    }
  }, [error]);

  // Fetch latest comments for all records
  // Comments are stored against applicationId, so we first resolve each record's applicationId via the ledger account
  useEffect(() => {
    const fetchLatestComments = async () => {
      const records = loanBookData?.content || [];
      const commentMap: LatestCommentMap = {};
      const appIdMap: ApplicationIdMap = {};

      await Promise.allSettled(
        records.map(async (record) => {
          if (!record.id) return;
          try {
            // Fetch the ledger account to get the applicationId
            const ledgerAccount = await getLedgerAccountById(record.id);
            const applicationId = ledgerAccount?.applicationId;
            if (!applicationId) return;

            appIdMap[record.id] = applicationId;

            // Fetch the latest comment using the applicationId
            const response = await getComments(applicationId, { page: 0, size: 1 });
            if (response?.content && response.content.length > 0) {
              commentMap[record.id] = response.content[0].commentText ?? '';
            }
          } catch {
            // Silently skip records without comments or missing accounts
          }
        })
      );

      setApplicationIdMap(appIdMap);
      setLatestComments(commentMap);
    };

    if (loanBookData?.content?.length) {
      fetchLatestComments();
    }
  }, [loanBookData]);

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + '-01');
    setSelectedMonth(newDate);
  };

  const formatSelectedMonth = (date: Date) => {
    return date.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  const handleCustomerNameSearch = (name: string) => {
    setCustomerName(name);
  };

  const clearFilters = () => {
    setCustomerName('');
    setFilterInvoicedOnSage(undefined);
    setFilterType('all');
  };

  const markLoanBookRecordAsInvoiced = async (id: string, sageNumber?: string) => {
    return customAxiosInstanceMutator<LoanBookRecord>({
      url: `/loan/api/v1/loan-book/records/${id}/mark-invoiced`,
      method: 'PATCH',
      ...(sageNumber && {
        headers: { 'Content-Type': 'application/json' },
        data: { sageInvoiceNumber: sageNumber },
      }),
    });
  };

  const handleMarkAsInvoiced = async (row: LoanBookRecord, sageNumber?: string) => {
    if (!row.id) {
      toast.error('Unable to update this loan record.');
      return;
    }
    if (row.invoicedOnSage) {
      toast.info(`${row.name || row.refNo || 'Loan'} is already marked as invoiced.`);
      return;
    }
    setActionLoadingState((current) => ({ ...current, [row.id!]: 'invoiced' }));
    try {
      await markLoanBookRecordAsInvoiced(row.id, sageNumber);
      toast.success(`${row.name || row.refNo || 'Loan'} marked as invoiced.${sageNumber ? ` Sage #: ${sageNumber}` : ''}`);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to mark as invoiced.');
    } finally {
      setActionLoadingState((current) => ({ ...current, [row.id!]: null }));
    }
  };

  const generateIdempotencyKey = (accountId: string) => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `loan-writeoff-${accountId}-${crypto.randomUUID()}`;
    }
    return `loan-writeoff-${accountId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getEffectiveRowStatus = (row: LoanBookRecord) => {
    const override = row.id ? statusOverrides[row.id] : undefined;
    return {
      disbursed: override?.disbursed ?? row.disbursed,
      invoicedOnSage: override?.invoicedOnSage ?? row.invoicedOnSage,
      status: override?.status ?? row.status,
    };
  };

  const handleWriteOff = async (row: LoanBookRecord) => {
    if (!row.id) {
      toast.error('Unable to write off this loan record.');
      return;
    }
    const rowLabel = row.name || row.refNo || 'Loan';
    const currentStatus = getEffectiveRowStatus(row);
    if ((currentStatus.status || '').toUpperCase().replace(/\s+/g, '_') === 'WRITTEN_OFF') {
      toast.info(`${rowLabel} is already written off.`);
      return;
    }

    const shouldWriteOff = window.confirm(`Write off ${rowLabel}? This will record a full account write-off.`);
    if (!shouldWriteOff) return;

    setActionLoadingState((current) => ({ ...current, [row.id!]: 'writeoff' }));
    try {
      await recordWriteOff({
        accountId: row.id,
        amount: 0,
        valueDate: toLocalDateString(),
        idempotencyKey: generateIdempotencyKey(row.id),
        remarks: `Loan write-off from loan invoices for ${rowLabel}`,
      });
      setStatusOverrides((current) => ({
        ...current,
        [row.id!]: {
          disbursed: currentStatus.disbursed,
          invoicedOnSage: currentStatus.invoicedOnSage,
          status: 'WRITTEN_OFF',
        },
      }));
      toast.success(`${rowLabel} written off successfully.`);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to write off loan.');
    } finally {
      setActionLoadingState((current) => ({ ...current, [row.id!]: null }));
    }
  };

  const handleAddCommentClick = (row: LoanBookRecord) => {
    setCommentTarget(row);
    setNewComment('');
    setCommentModalOpen(true);
  };

  const handleViewComments = async (row: LoanBookRecord) => {
    setViewCommentsTarget(row);
    setViewCommentsModalOpen(true);
    setIsLoadingComments(true);
    setAllComments([]);

    try {
      // Resolve applicationId for this record
      let applicationId = row.id ? applicationIdMap[row.id] : undefined;
      if (!applicationId && row.id) {
        const ledgerAccount = await getLedgerAccountById(row.id);
        applicationId = ledgerAccount?.applicationId;
        if (applicationId) {
          setApplicationIdMap((prev) => ({ ...prev, [row.id!]: applicationId! }));
        }
      }

      if (applicationId) {
        const response = await getComments(applicationId, { page: 0, size: 50 });
        if (response?.content) {
          setAllComments(response.content.map((c: any) => ({
            commentText: c.commentText || c.note || '',
            createdBy: c.createdBy,
            createdDate: c.createdDate || c.createdAt,
          })));
        }
      }
    } catch {
      // Silently handle - modal will show empty state
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentTarget?.id || !newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setIsSubmittingComment(true);
    try {
      // Resolve applicationId for this record
      let applicationId = applicationIdMap[commentTarget.id];
      if (!applicationId) {
        const ledgerAccount = await getLedgerAccountById(commentTarget.id);
        applicationId = ledgerAccount?.applicationId;
        if (applicationId) {
          setApplicationIdMap((prev) => ({ ...prev, [commentTarget.id!]: applicationId! }));
        }
      }

      if (!applicationId) {
        toast.error('Unable to find application ID for this loan record');
        setIsSubmittingComment(false);
        return;
      }

      const createdBy = user?.name || user?.username || undefined;
      await addComment(applicationId, newComment.trim(), createdBy);
      toast.success('Comment added successfully');
      setLatestComments((prev) => ({
        ...prev,
        [commentTarget.id!]: newComment.trim(),
      }));
      setCommentModalOpen(false);
      setCommentTarget(null);
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

    // Records come from the API already separated: loan entries and charge entries are distinct rows.
    // Charge entries have loanAmount === 0, parentReferenceNo !== refNo.
    const loanInvoiceRows = (loanBookData?.content || []).filter((row) => {
      if (filterType === 'loan') return !isChargeEntry(row);
      if (filterType === 'charge') return isChargeEntry(row);
      return true;
    });
    const useFixedHeader = loanInvoiceRows.length > 6;

    // Calculate totalCharges from loanBookData to ensure all charges (initial and additional) are included
    const totalCharges = loanBookData?.content?.reduce((sum, record) => sum + (record.charges || 0), 0) || 0;

    // Calculate totalLoanAmount from loanBookData
    const totalLoanAmount = loanBookData?.content?.reduce((sum, record) => sum + (record.loanAmount || 0), 0) || 0;

    // Calculate totalBalance from loanBookData
    const totalBalance = loanBookData?.content?.reduce((sum, record) => sum + (record.totalBalance || 0), 0) || 0;

  // Row Actions dropdown component
  const RowActions: React.FC<{ row: LoanBookRecord }> = ({ row }) => {
    const [show, setShow] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const targetRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const close = () => setShow(false);

    const onToggleClick = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShow((current) => !current);
    };

    useEffect(() => {
      if (!show) return;

      const updatePosition = () => {
        const target = targetRef.current;
        const menu = menuRef.current;
        if (!target || !menu) return;

        const spacing = 8;
        const rect = target.getBoundingClientRect();
        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = rect.right - menuWidth;
        if (left < spacing) left = spacing;
        if (left + menuWidth > viewportWidth - spacing) left = viewportWidth - menuWidth - spacing;

        const canOpenDown = rect.bottom + spacing + menuHeight <= viewportHeight - spacing;
        const canOpenUp = rect.top - spacing - menuHeight >= spacing;
        const top = !canOpenDown && canOpenUp
          ? rect.top - menuHeight - spacing
          : Math.min(rect.bottom + spacing, viewportHeight - menuHeight - spacing);

        setMenuStyle({ position: 'fixed', top, left, zIndex: 99999 });
      };

      const handlePointerDown = (event: MouseEvent) => {
        const target = event.target as Node;
        if (targetRef.current?.contains(target) || menuRef.current?.contains(target)) return;
        close();
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') close();
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleEscape);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
        document.removeEventListener('mousedown', handlePointerDown);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [show]);

    return (
      <div className="d-flex align-items-center">
        <button
          ref={targetRef}
          type="button"
          className="btn btn-sm btn-light rounded-pill d-flex align-items-center"
          onClick={onToggleClick}
        >
          <span>Actions</span>
          <i className="fa fa-caret-down ms-2"></i>
        </button>
        {show && typeof document !== 'undefined' && createPortal(
          <div
            ref={menuRef}
            className="loan-book-actions-menu"
            style={menuStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="loan-book-actions-item action-view"
              onClick={() => {
                close();
                navigate(`/loan-book/record/${row.id || ''}`);
              }}
            >
              <i className="fa fa-eye"></i>
              View Loan
            </button>
            {!getEffectiveRowStatus(row).invoicedOnSage && (
              <button
                type="button"
                className="loan-book-actions-item"
                disabled={actionLoadingState[row.id || ''] === 'invoiced'}
                onClick={() => {
                  close();
                  setSageInvoiceTarget(row);
                  setSageInvoiceNumber('');
                  setSageInvoiceModalOpen(true);
                }}
              >
                <i className={`fa ${actionLoadingState[row.id || ''] === 'invoiced' ? 'fa-spinner fa-spin' : 'fa-file-text-o'}`}></i>
                {actionLoadingState[row.id || ''] === 'invoiced' ? 'Marking...' : 'Mark as Invoiced'}
              </button>
            )}
            <button
              type="button"
              className="loan-book-actions-item"
              disabled={!!actionLoadingState[row.id || '']}
              onClick={() => {
                close();
                handleWriteOff(row);
              }}
            >
              <i className={`fa ${actionLoadingState[row.id || ''] === 'writeoff' ? 'fa-spinner fa-spin' : 'fa-ban'}`}></i>
              {actionLoadingState[row.id || ''] === 'writeoff' ? 'Writing Off...' : 'Write Off Loan'}
            </button>
            <button
              type="button"
              className="loan-book-actions-item"
              onClick={() => {
                setSageInfoTarget(row);
                setSageInfoModalOpen(true);
                close();
              }}
            >
              <i className="fa fa-info-circle"></i>
              Sage Invoice Info
            </button>
            <button
              type="button"
              className="loan-book-actions-item"
              onClick={() => {
                handleViewComments(row);
                close();
              }}
            >
              <i className="fa fa-comments"></i>
              View Comments
            </button>
            <button
              type="button"
              className="loan-book-actions-item"
              onClick={() => {
                handleAddCommentClick(row);
                close();
              }}
            >
              <i className="fa fa-comment"></i>
              Add Comment
            </button>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Column order matching the spreadsheet:
  // Type | Name | Loan Amount | Issued On | Charges | Total Balance | Ref No | Parent Reference No | Commencement Date | Due Date | Disbursed | Invoiced On Sage | Statement Date | Latest Comment | Actions
  const columns = [
    {
      name: 'Type',
      selector: (row: LoanBookRecord) => isChargeEntry(row) ? 'Charge' : 'Loan',
      sortable: true,
      cell: (row: LoanBookRecord) => {
        const isCharge = isChargeEntry(row);
        return (
          <span className={`badge px-2 py-1 ${isCharge ? 'bg-warning-subtle text-warning-emphasis' : 'bg-primary-subtle text-primary-emphasis'}`}>
            <i className={`fa ${isCharge ? 'fa-bolt' : 'fa-bank'} me-1`}></i>
            {isCharge ? 'Charge' : 'Loan'}
          </span>
        );
      },
      minWidth: '110px',
    },
    {
      name: 'Name',
      selector: (row: LoanBookRecord) => row.name || '',
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>
          {row.name || 'N/A'}
        </div>
      ),
      minWidth: '150px',
      wrap: true,
    },
    {
      name: 'Loan Amount',
      selector: (row: LoanBookRecord) => row.loanAmount || 0,
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div className="text-end" style={{ whiteSpace: 'nowrap' }}>
          {formatCurrency(row.loanAmount || 0)}
        </div>
      ),
      minWidth: '120px',
    },
    {
      name: 'Issued On',
      selector: (row: LoanBookRecord) => row.issuedOn || 0,
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div className="text-end" style={{ whiteSpace: 'nowrap' }}>
          {formatCurrency(row.issuedOn || 0)}
        </div>
      ),
      minWidth: '120px',
    },
    {
      name: 'Charges',
      selector: (row: LoanBookRecord) => row.charges || 0,
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div className="text-end" style={{ whiteSpace: 'nowrap' }}>
          {formatCurrency(row.charges || 0)}
        </div>
      ),
      minWidth: '110px',
    },
    {
      name: 'Total Balance',
      selector: (row: LoanBookRecord) => row.totalBalance || 0,
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div className="text-end fw-bold text-success" style={{ whiteSpace: 'nowrap' }}>
          {formatCurrency(row.totalBalance || 0)}
        </div>
      ),
      minWidth: '120px',
    },
    {
      name: 'Ref No',
      selector: (row: LoanBookRecord) => row.refNo || '',
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <span className="badge bg-light text-dark border px-2 py-1" title={row.refNo || 'N/A'} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', cursor: 'default' }}>
          {row.refNo || 'N/A'}
        </span>
      ),
      minWidth: '200px',
      maxWidth: '220px',
    },
    {
      name: 'Parent Reference No',
      selector: (row: LoanBookRecord) => row.parentReferenceNo || '',
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {row.parentReferenceNo || 'N/A'}
        </span>
      ),
      minWidth: '180px',
    },
    {
      name: 'Commencement Date',
      selector: (row: LoanBookRecord) => row.commencementDate || '',
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {row.commencementDate ? formatDate(row.commencementDate) : 'N/A'}
        </div>
      ),
      minWidth: '140px',
    },
    {
      name: 'Due Date',
      selector: (row: LoanBookRecord) => row.dueDate || '',
      sortable: true,
      cell: (row: LoanBookRecord) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {row.dueDate ? formatDate(row.dueDate) : 'N/A'}
        </div>
      ),
      minWidth: '120px',
    },
    {
      name: 'Actions',
      cell: (row: LoanBookRecord) => <RowActions row={row} />,
      minWidth: '140px',
      allowOverflow: true,
    },
  ];

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />

      {/* Add Comment Modal */}
      {commentModalOpen && commentTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-comment me-2 text-primary"></i>
                  Add Comment
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCommentModalOpen(false)}
                  disabled={isSubmittingComment}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Adding comment for: <strong>{commentTarget.name || commentTarget.refNo || 'Loan'}</strong>
                </p>
                <textarea
                  className="form-control"
                  rows={4}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment here..."
                  disabled={isSubmittingComment}
                  maxLength={2000}
                />
                <small className="text-muted">{newComment.length}/2000 characters</small>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCommentModalOpen(false)}
                  disabled={isSubmittingComment}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitComment}
                  disabled={isSubmittingComment || !newComment.trim()}
                >
                  {isSubmittingComment ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-plus me-2"></i>
                      Add Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sage Invoice Info Modal */}
      {sageInfoModalOpen && sageInfoTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#cfe2ff', border: '1px solid rgba(13,110,253,0.2)' }}>
                    <i className="fa fa-file-text-o text-primary" style={{ display: 'block' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">Sage Invoice Info</h5>
                    <p className="text-muted mb-0 small">{sageInfoTarget.name || sageInfoTarget.refNo || 'Loan'}</p>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setSageInfoModalOpen(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  <div className="col-6">
                    <small className="text-muted d-block">Invoiced on Sage</small>
                    <span className={`badge px-2 py-1 ${sageInfoTarget.invoicedOnSage ? 'bg-success-subtle text-success-emphasis' : 'bg-light text-muted border'}`}>
                      {sageInfoTarget.invoicedOnSage ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Sage Invoice #</small>
                    <span className="fw-semibold">{sageInfoTarget.sageInvoiceNumber || '—'}</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Statement Date</small>
                    <span className="fw-semibold">{sageInfoTarget.commencementDate ? formatDate(sageInfoTarget.commencementDate) : 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setSageInfoModalOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Comments Modal */}
      {viewCommentsModalOpen && viewCommentsTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.2)' }}>
                    <i className="fa fa-comments text-success" style={{ display: 'block' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">Comments</h5>
                    <p className="text-muted mb-0 small">{viewCommentsTarget.name || viewCommentsTarget.refNo || 'Loan'}</p>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setViewCommentsModalOpen(false)}></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {isLoadingComments ? (
                  <div className="text-center py-4">
                    <span className="spinner-border text-success" role="status"></span>
                    <p className="text-muted mt-3">Loading comments...</p>
                  </div>
                ) : allComments.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {allComments.map((comment, index) => (
                      <div key={index} className="card border shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            {comment.createdBy && (
                              <small className="text-muted">
                                <i className="fa fa-user me-1"></i>{comment.createdBy}
                              </small>
                            )}
                            {comment.createdDate && (
                              <small className="text-muted">
                                {formatDate(comment.createdDate)}
                              </small>
                            )}
                          </div>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{comment.commentText}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fa fa-comments fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                    <p className="text-muted">No comments found for this loan.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setViewCommentsModalOpen(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary rounded-pill px-4"
                  onClick={() => {
                    setViewCommentsModalOpen(false);
                    handleAddCommentClick(viewCommentsTarget);
                  }}
                >
                  <i className="fa fa-plus me-2"></i>Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/">
                    <i className="fa fa-home me-2"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/loan-book/summary">Loan Book</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">Loan Invoices</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="fs-2 fw-bold mb-2 text-white">
                Loan Invoices 📋
              </h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Consolidated financial view of all loans and charges for {formatSelectedMonth(selectedMonth)}.
                Charges appear as separate line items in the month they were applied.
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="d-flex gap-3 justify-content-md-end align-items-center">
                <div className="d-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-calendar me-2 text-primary"></i>
                  <input
                    type="month"
                    className="border-0 bg-transparent fw-medium"
                    style={{ outline: 'none', minWidth: '120px' }}
                    value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                    onChange={handleMonthChange}
                  />
                </div>
                <div className="d-inline-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-clock-o me-2 text-success"></i>
                  <span className="fw-medium">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">

          {/* Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-primary rounded-3 p-3 me-3">
                      <i className="fa fa-search text-primary fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Search & Filter</h5>
                      <p className="text-muted mb-0 small">Find specific loan invoices</p>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-3">
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-lg-5">
                      <label className="form-label fw-semibold text-dark">
                        <i className="fa fa-user me-2 text-muted"></i>Customer Search
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0">
                          <i className="fa fa-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          placeholder="Search by customer name or loan reference..."
                          value={customerName}
                          onChange={(e) => handleCustomerNameSearch(e.target.value)}
                          style={{ boxShadow: 'none' }}
                        />
                        {customerName && (
                          <button
                            className="btn btn-outline-secondary border-start-0"
                            type="button"
                            onClick={() => handleCustomerNameSearch('')}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-2">
                      <label className="form-label fw-semibold text-dark">
                        <i className="fa fa-filter me-2 text-muted"></i>Type
                      </label>
                      <select
                        className="form-select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as 'all' | 'loan' | 'charge')}
                        style={{ boxShadow: 'none' }}
                      >
                        <option value="all">All</option>
                        <option value="loan">Loans Only</option>
                        <option value="charge">Charges Only</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="form-label fw-semibold text-dark">
                        <i className="fa fa-file-text-o me-2 text-muted"></i>Sage Invoice
                      </label>
                      <select
                        className="form-select"
                        value={filterInvoicedOnSage === undefined ? '' : filterInvoicedOnSage ? 'true' : 'false'}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFilterInvoicedOnSage(val === '' ? undefined : val === 'true');
                        }}
                        style={{ boxShadow: 'none' }}
                      >
                        <option value="">All</option>
                        <option value="true">Invoiced on Sage</option>
                        <option value="false">Not Invoiced</option>
                      </select>
                    </div>
                    <div className="col-12 col-lg-4">
                      <div className="d-flex gap-2 justify-content-lg-end">
                        <button
                          className="btn btn-outline-secondary d-flex align-items-center"
                          onClick={clearFilters}
                          style={{ minWidth: '100px' }}
                        >
                          <i className="fa fa-refresh me-2"></i>Clear
                        </button>
                        <button
                          className="btn btn-primary d-flex align-items-center"
                          onClick={() => refetch()}
                          style={{ minWidth: '100px' }}
                        >
                          <i className="fa fa-search me-2"></i>Search
                        </button>
                      </div>
                    </div>
                  </div>
                  {(customerName || filterInvoicedOnSage !== undefined || filterType !== 'all') && (
                    <div className="mt-3 p-3 bg-light rounded-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted fw-medium">Active Filters:</small>
                          {filterType !== 'all' && (
                            <span className="badge bg-primary-subtle text-primary">
                              Type: {filterType === 'loan' ? 'Loans' : 'Charges'}
                            </span>
                          )}
                          {filterInvoicedOnSage !== undefined && (
                            <span className="badge bg-success-subtle text-success">
                              Sage: {filterInvoicedOnSage ? 'Invoiced' : 'Not Invoiced'}
                            </span>
                          )}
                          {customerName && (
                            <span className="badge bg-info-subtle text-info">
                              Search: "{customerName}"
                            </span>
                          )}
                        </div>
                        <button className="btn btn-sm btn-link text-decoration-none p-0" onClick={clearFilters}>
                          <i className="fa fa-times me-1"></i>Clear All
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

                    {/* Summary Cards */}
          {(loanBookSummary || loanBookData) && (
            <div className="row g-4 mb-4">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Total Loans</p>
                        <h3 className="mb-0 fw-bold text-info">
                          {(loanBookData?.content?.length || 0).toLocaleString()}
                          {loanBookData?.content && loanBookData.content.length > 0 && (
                            <span className="fs-6 fw-normal text-muted ms-2">
                              ({loanBookData.content.filter(r => !isChargeEntry(r)).length} loans)
                            </span>
                          )}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(13,202,240,0.2)' }}>
                        <i className="fa fa-file-text-o fa-2x text-info" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Total Loan Amount</p>
                        <h3 className="mb-0 fw-bold text-primary">
                          {formatCurrency(totalLoanAmount)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(13,110,253,0.2)' }}>
                        <i className="fa fa-money fa-2x text-primary" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Total Charges</p>
                        <h3 className="mb-0 fw-bold text-warning">
                          {formatCurrency(totalCharges)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                        <i className="fa fa-credit-card fa-2x text-warning" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">Total Balance</p>
                        <h3 className="mb-0 fw-bold text-success">
                          {formatCurrency(totalBalance)}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(25,135,84,0.2)' }}>
                        <i className="fa fa-balance-scale fa-2x text-success" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-transparent border-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="bg-light-primary rounded-3 p-3 me-3">
                        <i className="fa fa-list text-primary fs-4"></i>
                      </div>
                      <div>
                        <h5 className="card-title mb-0">Loan Invoices</h5>
                        <p className="text-muted mb-0 small">
                          {loanInvoiceRows.length} record{loanInvoiceRows.length !== 1 ? 's' : ''} found
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <DataTable
                    columns={columns}
                    data={loanInvoiceRows}
                    pagination
                    paginationPerPage={size}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={isLoading}
                    progressComponent={
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading loan invoices...</p>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center py-5">
                        <i className="fa fa-inbox fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                        <h5 className="text-muted">No loan invoices found</h5>
                        <p className="text-muted small">Try adjusting your search criteria or selecting a different month.</p>
                      </div>
                    }
                    highlightOnHover
                    striped
                    responsive
                    fixedHeader={useFixedHeader}
                    fixedHeaderScrollHeight="600px"
                    dense
                    pointerOnHover
                    customStyles={{
                      headCells: {
                        style: {
                          fontWeight: '600',
                          fontSize: '0.8rem',
                          color: '#495057',
                          backgroundColor: '#f8f9fa',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                        },
                      },
                      cells: {
                        style: {
                          fontSize: '0.85rem',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Sage Invoice Modal */}
      {sageInvoiceModalOpen && sageInvoiceTarget && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-file-text-o me-2 text-primary"></i>Mark as Invoiced on Sage
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSageInvoiceModalOpen(false);
                    setSageInvoiceTarget(null);
                    setSageInvoiceNumber('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Marking <strong>{sageInvoiceTarget.name || sageInvoiceTarget.refNo || 'Loan'}</strong> as invoiced on Sage.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Sage Invoice Number</label>
                  <div className="input-group">
                    <span className="input-group-text fw-semibold">INV</span>
                    <input
                      type="text"
                      className={`form-control ${sageInvoiceNumber && !/^\d{7}$/.test(sageInvoiceNumber) ? 'is-invalid' : sageInvoiceNumber ? 'is-valid' : ''}`}
                      placeholder="0001821"
                      value={sageInvoiceNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 7);
                        setSageInvoiceNumber(val);
                      }}
                      maxLength={7}
                      autoFocus
                    />
                  </div>
                  <small className="text-muted">Format: INV followed by 7 digits (e.g., INV2047585)</small>
                  {sageInvoiceNumber && !/^\d{7}$/.test(sageInvoiceNumber) && (
                    <small className="text-danger d-block mt-1">
                      <i className="fa fa-exclamation-circle me-1"></i>
                      Please enter exactly 7 digits
                    </small>
                  )}
                  {sageInvoiceNumber && /^\d{7}$/.test(sageInvoiceNumber) && (
                    <small className="text-success d-block mt-1">
                      <i className="fa fa-check-circle me-1"></i>
                      Invoice number: INV{sageInvoiceNumber}
                    </small>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSageInvoiceModalOpen(false);
                    setSageInvoiceTarget(null);
                    setSageInvoiceNumber('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!/^\d{7}$/.test(sageInvoiceNumber)}
                  onClick={() => {
                    const target = sageInvoiceTarget;
                    const number = `INV${sageInvoiceNumber.trim()}`;
                    setSageInvoiceModalOpen(false);
                    setSageInvoiceTarget(null);
                    setSageInvoiceNumber('');
                    void handleMarkAsInvoiced(target, number);
                  }}
                >
                  <i className="fa fa-check me-2"></i>Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanInvoices;